import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const parseDateWithOffset = (dateString: string) => {
  return dayjs(dateString);
};

export const formatDateWithOffset = (date: any) => {
  return dayjs(date).format(); // Default ISO 8601 format with offset
};

export const getUtcDate = (date: any) => {
  return dayjs(date).utc().format();
};

export const getLocalDate = (date: any) => {
  return dayjs(date).local().format();
};

export const getSpecificTimeZoneDate = (date: any, timeZone: string) => {
  return dayjs(date).tz(timeZone).format();
};

export const serviceOrderChangeDateParser = (dateTimeRange: string) => {
  if (typeof dateTimeRange !== 'string') {
    return undefined;
  }
  // Regular expression to match the expected format
  const dateTimeRangeRegex = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} - \d{2}:\d{2}$/;

  // Check if the input string matches the expected format
  if (!dateTimeRange.match(dateTimeRangeRegex)) {
    return undefined;
  }

  const [datePart, fromTime, _spliter, toTime] = dateTimeRange.split(' ');

  // Combine date part with from and to times to create full date-time strings
  const fromDateTimeStr = `${datePart} ${fromTime}`;
  const toDateTimeStr = `${datePart} ${toTime}`;

  // Parse the date and time parts as UTC
  const fromDateTime = dayjs.utc(fromDateTimeStr, 'DD/MM/YYYY HH:mm');
  const toDateTime = dayjs.utc(toDateTimeStr, 'DD/MM/YYYY HH:mm');

  // Convert to local timezone
  const fromDateTimeLocal = fromDateTime.local();
  const toDateTimeLocal = toDateTime.local();

  // Additional validation to ensure the parsed dates are valid
  if (!fromDateTimeLocal.isValid() || !toDateTimeLocal.isValid()) {
    return undefined;
  }

  // Combine date and time into the desired format
  const combinedString = `${fromDateTimeLocal.format('DD/MM/YYYY')} ${fromDateTimeLocal.format('HH:mm')} - ${toDateTimeLocal.format('HH:mm')}`;

  return combinedString;
};

export const getDateRangeFromPreset = (range?: string): { startAt?: string; endAt?: string } => {
  if (!range) return {};
  const now = dayjs();
  const fmt = (d: dayjs.Dayjs) => d.toISOString();
  switch (range) {
    case 'today':
      return { startAt: fmt(now.startOf('day')), endAt: fmt(now.endOf('day')) };
    case 'this_week':
      return { startAt: fmt(now.startOf('week')), endAt: fmt(now.endOf('week')) };
    case 'this_month':
      return { startAt: fmt(now.startOf('month')), endAt: fmt(now.endOf('month')) };
    case 'last_month':
      return { startAt: fmt(now.subtract(1, 'month').startOf('month')), endAt: fmt(now.subtract(1, 'month').endOf('month')) };
    case 'last_3_months':
      return { startAt: fmt(now.subtract(3, 'month').startOf('month')), endAt: fmt(now.endOf('day')) };
    case 'last_6_months':
      return { startAt: fmt(now.subtract(6, 'month').startOf('month')), endAt: fmt(now.endOf('day')) };
    case 'this_year':
      return { startAt: fmt(now.startOf('year')), endAt: fmt(now.endOf('year')) };
    case 'last_year':
      return { startAt: fmt(now.subtract(1, 'year').startOf('year')), endAt: fmt(now.subtract(1, 'year').endOf('year')) };
    default:
      return {};
  }
};

export const roundUpToNext15 = (date: dayjs.Dayjs) => {
  const remainder = 15 - (date.minute() % 15);

  return remainder === 15 ? date.second(0) : date.add(remainder, 'minute').second(0);
};
